// @ts-nocheck

import { ThemeEvents, CartAddEvent } from '@theme/events';

const CONFIG = window.giftCardConfigurations || {};
const COUNTRY = window.Shopify?.country;
const SHOULD_RUN =
  Boolean(CONFIG.productHandle && CONFIG.revenueStampAmount) && COUNTRY === 'IT';

let isAdjustingCart = false;
let latestCartSnapshot = null;

const parseThresholdToCents = (value) => {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return Math.round(value);
  const normalized = String(value).trim();
  if (!normalized.length) return 0;

  if (normalized.includes('.') || normalized.includes(',')) {
    const decimalValue = Number(normalized.replace(',', '.'));
    if (Number.isNaN(decimalValue)) return 0;
    return Math.round(decimalValue * 100);
  }

  const intValue = Number(normalized);
  return Number.isNaN(intValue) ? 0 : Math.round(intValue);
};

const getLineUnitPrice = (item) => {
  const quantity = Number(item.quantity) || 1;
  const linePrice =
    Number(item.final_line_price ?? item.line_price ?? item.price) || 0;
  return Math.round(linePrice / quantity);
};

const isGiftCardLine = (item) => {
  if (!item) return false;
  if (item.gift_card) return true;
  const productType = item.product_type || item.product?.product_type;
  if (productType && typeof productType === 'string') {
    return productType.toLowerCase().includes('gift');
  }
  return false;
};

async function checkTaxProduct() {
  return syncRevenueStamp();
}

async function checkGiftCardProduct() {
  latestCartSnapshot = null;
  return syncRevenueStamp();
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function syncRevenueStamp() {
  if (!SHOULD_RUN || isAdjustingCart) return null;

  isAdjustingCart = true;
  let mutationResponse = null;
  try {
    latestCartSnapshot = await fetchCartData();
    const cartData = latestCartSnapshot;
    if (!cartData) return null;

    const { hasQualifyingGiftCard, taxProductIndex } = await evaluateCartState(cartData);

    if (hasQualifyingGiftCard && taxProductIndex === -1) {
      mutationResponse = await addRevenueStampProduct();
    } else if (!hasQualifyingGiftCard && taxProductIndex !== -1) {
      const confirmedState = await recheckBeforeRemoval();
      if (
        confirmedState &&
        !confirmedState.hasQualifyingGiftCard &&
        confirmedState.taxProductIndex !== -1
      ) {
        mutationResponse = await removeRevenueStampProduct(confirmedState.taxProductIndex + 1);
      }
    }
  } catch (error) {
    console.error('Gift card duty sync failed:', error);
  } finally {
    isAdjustingCart = false;
  }
  return mutationResponse;
}

async function addRevenueStampProduct() {
  const taxProduct = await getTaxProduct();
  const variantId = taxProduct?.variants?.[0]?.id;
  if (!variantId) return null;

  const response = await addTaxProductToCart(variantId, 1);
  return handleCartResponse(response);
}

async function removeRevenueStampProduct(line) {
  const response = await removeProductFromCart(line);
  let finalResponse = handleCartResponse(response);
  const cartAfterRemoval = await fetchCartData();
  if (cartAfterRemoval) {
    const { hasQualifyingGiftCard } = await evaluateCartState(cartAfterRemoval);
    if (hasQualifyingGiftCard) {
      finalResponse = await addRevenueStampProduct();
    }
  }
  return finalResponse;
}

async function fetchCartData() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Error fetching cart data: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function getTaxProduct() {
  if (window.taxProduct?.variants?.length) {
    return window.taxProduct;
  }

  const handle = CONFIG.productHandle;
  if (!handle) return null;

  try {
    const root = window.Shopify?.routes?.root ?? '/';
    const response = await fetch(`${root}products/${handle}.js`);
    if (!response.ok) throw new Error(`Unable to fetch product ${handle}`);
    const product = await response.json();
    window.taxProduct = product;
    return product;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function addTaxProductToCart(productId, quantity) {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: productId,
        quantity,
        properties: {
          _gift_card_duty: true,
        },
        sections: getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    });
    return await response.json();
  } catch (error) {
    console.error(`Error adding product to cart: ${error}`);
    return null;
  }
}

async function removeProductFromCart(productLine) {
  try {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line: productLine,
        quantity: 0,
        sections: getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    });
    return await response.json();
  } catch (error) {
    console.error(`Error removing product from cart: ${error}`);
    return null;
  }
}

function handleCartResponse(response) {
  if (!response) return null;

  const sections = response.sections || {};
  const itemCount =
    response.item_count ??
    response.items?.reduce((total, item) => total + (item.quantity || 0), 0) ??
    0;

  document.dispatchEvent(
    new CartAddEvent(response, 'gift-card-duty', {
      source: 'gift-card-duty',
      itemCount,
      sections,
    })
  );
  return response;
}

function getSectionsToRender() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sections.add(component.dataset.sectionId);
    }
  });

  return Array.from(sections).map((id) => ({ id }));
}

function evaluateCartLines(items) {
  const revenueThreshold = parseThresholdToCents(CONFIG.revenueStampAmount);
  const hasQualifyingGiftCard = items.some(
    (item) => isGiftCardLine(item) && getLineUnitPrice(item) >= revenueThreshold
  );
  const taxProductIndex = items.findIndex((item) => item.handle === CONFIG.productHandle);

  return { hasQualifyingGiftCard, taxProductIndex };
}

async function evaluateCartState(cartData) {
  const items = cartData.items || [];
  return evaluateCartLines(items);
}

async function recheckBeforeRemoval() {
  await delay(400);
  const freshCart = latestCartSnapshot ?? (await fetchCartData());
  if (!freshCart) {
    return { hasQualifyingGiftCard: false, taxProductIndex: -1 };
  }
  latestCartSnapshot = freshCart;
  return evaluateCartState(freshCart);
}

if (SHOULD_RUN) {
  const initSync = () => {
    syncRevenueStamp();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSync, { once: true });
  } else {
    initSync();
  }

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    latestCartSnapshot = null;
    if (isAdjustingCart) return;
    syncRevenueStamp();
  });
}

window.parisTexasGiftCard = {
  checkTaxProduct,
  checkGiftCardProduct,
  syncDuty: syncRevenueStamp,
};
